import type {
  HorizonConfig,
  HorizonEventLog,
  HorizonIngestApp,
  HorizonOptOutDateRange,
  HorizonOptOutDetails,
} from '../types/horizonOptOutTracker';

const eventTypes = {
  existingLeadExpired: 'existingleadfound_expired',
  phaseRulesEvaluated: 'phaserulesevaluated',
  worthEvaluated: 'worthaicheckevaluated',
} as const;

const eventOutcomes = {
  failed: 'failed',
  passed: 'passed',
} as const;

interface HorizonOptOutCsvRow {
  companyName: string;
  email: string;
  optOutReason: string;
  optOutTime: string;
  region: string;
  type: string;
}

const csvColumns: { header: string; key: keyof HorizonOptOutCsvRow }[] = [
  { header: 'Email', key: 'email' },
  { header: 'Company name', key: 'companyName' },
  { header: 'Region', key: 'region' },
  { header: 'Opt out reason', key: 'optOutReason' },
  { header: 'Type', key: 'type' },
  { header: 'Time of opt out (UTC)', key: 'optOutTime' },
];

const escapeCsvValue = (value: string): string => {
  const safeValue = /^[=+\-@\t\r]/.test(value) ? `'${value}` : value;
  return /[",\r\n]/.test(safeValue) ? `"${safeValue.replaceAll('"', '""')}"` : safeValue;
};

/** Serializes the visible Horizon opt-out grid values as a UTF-8 CSV document. */
export const createHorizonOptOutCsv = (rows: HorizonOptOutCsvRow[]): string =>
  [
    csvColumns.map(({ header }) => escapeCsvValue(header)).join(','),
    ...rows.map((row) => csvColumns.map(({ key }) => escapeCsvValue(row[key])).join(',')),
  ].join('\r\n');

const horizonConfigDefinitions = [
  { name: 'Horizon_US', region: 'US' },
  { name: 'Horizon_UK', region: 'UK' },
] as const;

const readValue = (value: unknown, keys: string[]): unknown => {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return undefined;

  const record = value as Record<string, unknown>;
  const recordKeys = Object.keys(record);
  const key = keys
    .map((expected) => recordKeys.find((candidate) => candidate.toLowerCase() === expected.toLowerCase()))
    .find((candidate) => candidate !== undefined);
  return key ? record[key] : undefined;
};

const readString = (value: unknown, keys: string[]): string | undefined => {
  const candidate = readValue(value, keys);
  return typeof candidate === 'string' && candidate.trim() ? candidate.trim() : undefined;
};

const readInteger = (value: unknown, keys: string[]): number | undefined => {
  const candidate = readValue(value, keys);
  return typeof candidate === 'number' && Number.isSafeInteger(candidate) && candidate >= 0 ? candidate : undefined;
};

const isUuid = (value: string): boolean =>
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value);

const readCompanyName = (companyNameJson: unknown): string => {
  if (typeof companyNameJson !== 'string' || !companyNameJson.trim()) return 'N/A';

  try {
    const parsedValue: unknown = JSON.parse(companyNameJson);
    if (typeof parsedValue === 'string' && parsedValue.trim()) return parsedValue.trim();

    const namedValue = readString(parsedValue, [
      'companyDbaName',
      'companyLegalName',
      'companyName',
      'legalName',
      'dbaName',
      'doingBusinessAs',
      'name',
    ]);
    return namedValue ?? 'N/A';
  } catch {
    const trimmedValue = companyNameJson.trim();
    return trimmedValue.startsWith('{') || trimmedValue.startsWith('[') ? 'N/A' : trimmedValue;
  }
};

/**
 * Resolves the supported Horizon configurations present in the config-list response.
 *
 * Each environment seeds its own subset of Horizon configurations, so an absent configuration is
 * expected and simply omitted. A duplicated name or an invalid ID still indicates a broken response.
 */
export const resolveHorizonConfigs = (response: unknown): HorizonConfig[] => {
  if (!Array.isArray(response)) throw new Error('Kirby returned an invalid configuration list.');

  return horizonConfigDefinitions.flatMap(({ name, region }) => {
    const matches = response.filter((config) => readString(config, ['configName']) === name);
    if (matches.length === 0) return [];
    if (matches.length > 1) throw new Error(`Expected exactly one ${name} configuration.`);

    const id = readInteger(matches[0], ['id']);
    if (id === undefined) throw new Error(`${name} has an invalid configuration ID.`);
    return [{ id, name, region }];
  });
};

/** Normalizes the IngestApp fields required by the dashboard. */
export const normalizeHorizonIngestApp = (
  record: Record<string, unknown>,
  config: HorizonConfig,
): HorizonIngestApp | null => {
  const appUid = readString(record, ['appUid']);
  if (!appUid || !isUuid(appUid)) return null;

  const recordConfigId = readInteger(record, ['onDemandConfigId']);
  if (recordConfigId !== undefined && recordConfigId !== config.id) return null;

  return {
    appUid,
    companyName: readCompanyName(readValue(record, ['companyNameJson'])),
    email: readString(record, ['email']) ?? 'N/A',
    region: config.region,
  };
};

/** Normalizes the EventLog fields required by the dashboard. */
export const normalizeHorizonEventLog = (record: Record<string, unknown>): HorizonEventLog | null => {
  const appUid = readString(record, ['appUid']);
  const eventDateUTC = readString(record, ['eventDateUTC']);
  const eventType = readString(record, ['eventType']);
  const eventDescription = readString(record, ['eventDescription']);
  if (
    !appUid ||
    !isUuid(appUid) ||
    !eventDateUTC ||
    Number.isNaN(Date.parse(eventDateUTC)) ||
    !eventType ||
    !eventDescription
  ) {
    return null;
  }

  return { appUid, eventDateUTC, eventType, eventDescription };
};

/** Returns the default ten-day inclusive calendar range in UTC. */
export const getDefaultHorizonOptOutDateRange = (now = new Date()): HorizonOptOutDateRange => ({
  endDate: now.toISOString().slice(0, 10),
  startDate: new Date(now.getTime() - 9 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10),
});

/** Converts selected calendar dates to inclusive UTC timestamps for Kirby filtering. */
export const getHorizonOptOutDateBounds = (
  dateRange: HorizonOptOutDateRange,
): { windowEnd: string; windowStart: string } => {
  const windowStart = new Date(`${dateRange.startDate}T00:00:00.000Z`);
  const windowEnd = new Date(`${dateRange.endDate}T23:59:59.999Z`);
  if (Number.isNaN(windowStart.getTime()) || Number.isNaN(windowEnd.getTime()) || windowStart > windowEnd) {
    throw new Error('A valid Horizon opt-out date range is required.');
  }

  return { windowEnd: windowEnd.toISOString(), windowStart: windowStart.toISOString() };
};

/** Selects the latest qualifying Worth event, falling back to the latest Global Payments opt-out event. */
const selectOptOutEvent = (events: HorizonEventLog[]): HorizonEventLog | undefined => {
  const worthEvents = events.filter(
    (event) =>
      event.eventType.toLowerCase() === eventTypes.worthEvaluated &&
      !event.eventDescription.toLowerCase().includes(eventOutcomes.passed),
  );
  const globalPaymentsEvents = events.filter(
    (event) =>
      event.eventType.toLowerCase() === eventTypes.existingLeadExpired ||
      (event.eventType.toLowerCase() === eventTypes.phaseRulesEvaluated &&
        event.eventDescription.toLowerCase().includes(eventOutcomes.failed)),
  );
  const candidates = worthEvents.length > 0 ? worthEvents : globalPaymentsEvents;

  return candidates.reduce<HorizonEventLog | undefined>((latest, event) => {
    if (!latest || Date.parse(event.eventDateUTC) > Date.parse(latest.eventDateUTC)) return event;
    return latest;
  }, undefined);
};

/** Creates the progressively loaded details for a selected opt-out event. */
export const createHorizonOptOutDetails = (events: HorizonEventLog[]): HorizonOptOutDetails | null => {
  const selectedEvent = selectOptOutEvent(events);
  if (!selectedEvent) return null;

  return {
    optOutReason: selectedEvent.eventDescription,
    optOutTimeUTC: selectedEvent.eventDateUTC,
    type: selectedEvent.eventType.toLowerCase() === eventTypes.worthEvaluated ? 'Worth' : 'Global Payments',
  };
};
